import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nsOptions } from '../i18n';
import {
  CAN_INVITE_TEAM_MEMBER, CAN_CREATE_PROJECT, CAN_MANAGE_TEAM_PERMISSIONS,
  CAN_VIEW_PROJECTS, CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
  CAN_VIEW_RESEARCH_TEAMS, CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
  CAN_MANAGE_PUBLIC_PROFILE,
} from '../constants';
import LabeledChoice, { eventToMultipleChoicesValue } from './LabeledChoice';
import Help from './Help';

@withTranslation('', nsOptions)
class TeamMemberPermissionSettings extends Component {
    static propTypes = {
      t: PropTypes.func.isRequired,
      className: PropTypes.string,
      labelClassName: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
      defaultValue: PropTypes.arrayOf(PropTypes.string),
      onChange: PropTypes.func,
      readOnly: PropTypes.bool,
      writablePermissions: PropTypes.arrayOf(PropTypes.string),
      // eslint-disable-next-line react/no-unused-prop-types
      isLeader: PropTypes.bool,
      isOwnerAndLeader: PropTypes.bool,
      showOwnerOnlyMessage: PropTypes.bool,
      showCannotEditOwnPermissions: PropTypes.bool,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
      isAdminTeam: PropTypes.bool,
      isInstitution: PropTypes.bool,
    };

    static defaultProps = {
      className: '',
      labelClassName: '',
      value: [],
      defaultValue: undefined,
      onChange: async () => true,
      readOnly: false,
      writablePermissions: [],
      isLeader: false,
      isOwnerAndLeader: false,
      showOwnerOnlyMessage: false,
      showCannotEditOwnPermissions: false,
      title: 'user:team.permissions.title',
      isAdminTeam: false,
      isInstitution: false,
    };

    static getTeamPermissionList = () => [
      CAN_CREATE_PROJECT,
      CAN_INVITE_TEAM_MEMBER,
      CAN_MANAGE_TEAM_PERMISSIONS,
    ];

    constructor(props) {
      super(props);
      this.state = {
        internalValue: props.defaultValue,
      };
    }

    onPermissionChange = async (e) => {
      const { isOwnerAndLeader } = this.props;

      if (isOwnerAndLeader) {
        return false;
      }

      e.persist();
      const { internalValue } = this.state;
      const prevInternalValue = internalValue;

      if (internalValue) {
        this.setState({ internalValue: eventToMultipleChoicesValue(e, internalValue) });
      }

      const success = await this.props.onChange(e);

      if (!success && prevInternalValue) {
        // Annuler le changement en cas d'erreur
        this.setState({ internalValue: prevInternalValue });
      }

      return success;
    };

    clearInternalValue = () => {
      this.setState({ internalValue: [] });
    };

    getChoices = () => {
      const { t, isAdminTeam } = this.props;

      // Standard team permissions
      const standardChoices = [
        {
          text: t('user:team.permissions.can_create_project'),
          value: CAN_CREATE_PROJECT,
        },
        {
          text: (
            <span>
              {t('user:team.permissions.can_invite_team_member')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:team.permissions.can_invite_team_member_help')}
              </Help>
            </span>
          ),
          value: CAN_INVITE_TEAM_MEMBER,
        },
        {
          text: (
            <span>
              {t('user:team.permissions.can_manage_team_permissions')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:team.permissions.can_manage_team_permissions_help')}
              </Help>
            </span>
          ),
          value: CAN_MANAGE_TEAM_PERMISSIONS,
        },
      ];

      // Admin team permissions - return all in flat structure for compatibility
      if (isAdminTeam) {
        return this.getAdminTeamChoicesFlat();
      }

      return standardChoices;
    };

    getAdminTeamChoicesFlat = () => {
      const { t, isInstitution } = this.props;

      const choices = [
        // Section 1: Profile and team management
        {
          text: (
            <span>
              {t('user:admin-team.permissions.can_manage_public_profile')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:admin-team.permissions.can_manage_public_profile_help')}
              </Help>
            </span>
          ),
          value: CAN_MANAGE_PUBLIC_PROFILE,
        },
        {
          text: (
            <span>
              {t('user:admin-team.permissions.can_invite_team_member')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:admin-team.permissions.can_invite_team_member_help')}
              </Help>
            </span>
          ),
          value: CAN_INVITE_TEAM_MEMBER,
        },
        {
          text: (
            <span>
              {t('user:admin-team.permissions.can_manage_team_permissions')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:admin-team.permissions.can_manage_team_permissions_help')}
              </Help>
            </span>
          ),
          value: CAN_MANAGE_TEAM_PERMISSIONS,
        },
        // Section 2: Projects
        {
          text: (
            <span>
              {t('user:admin-team.permissions.can_view_projects')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:admin-team.permissions.can_view_projects_help')}
              </Help>
            </span>
          ),
          value: CAN_VIEW_PROJECTS,
        },
        {
          text: (
            <span>
              {t('user:admin-team.permissions.can_create_and_administrate_projects')}
              <Help iconClassName="ml-2 align-middle">
                {t('user:admin-team.permissions.can_create_and_administrate_projects_help')}
              </Help>
            </span>
          ),
          value: CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
        },
      ];

      // Section 3: Research teams (institution only)
      if (isInstitution) {
        choices.push(
          {
            text: (
              <span>
                {t('user:admin-team.permissions.can_view_research_teams')}
                <Help iconClassName="ml-2 align-middle">
                  {t('user:admin-team.permissions.can_view_research_teams_help')}
                </Help>
              </span>
            ),
            value: CAN_VIEW_RESEARCH_TEAMS,
          },
          {
            text: (
              <span>
                {t('user:admin-team.permissions.can_create_and_administrate_research_teams')}
                <Help iconClassName="ml-2 align-middle">
                  {t('user:admin-team.permissions.can_create_and_administrate_research_teams_help')}
                </Help>
              </span>
            ),
            value: CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
          },
        );
      }

      return choices;
    };

    getAdminTeamSections = () => {
      const { t, isInstitution } = this.props;

      const sections = [
        {
          title: t('user:admin-team.permissions.section.profile-and-team'),
          choices: [
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_manage_public_profile')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_manage_public_profile_help')}
                  </Help>
                </span>
              ),
              value: CAN_MANAGE_PUBLIC_PROFILE,
            },
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_invite_team_member')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_invite_team_member_help')}
                  </Help>
                </span>
              ),
              value: CAN_INVITE_TEAM_MEMBER,
            },
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_manage_team_permissions')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_manage_team_permissions_help')}
                  </Help>
                </span>
              ),
              value: CAN_MANAGE_TEAM_PERMISSIONS,
            },
          ],
        },
        {
          title: t('user:admin-team.permissions.section.projects'),
          choices: [
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_view_projects')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_view_projects_help')}
                  </Help>
                </span>
              ),
              value: CAN_VIEW_PROJECTS,
            },
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_create_and_administrate_projects')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_create_and_administrate_projects_help')}
                  </Help>
                </span>
              ),
              value: CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
            },
          ],
        },
      ];

      // Research teams section (institution only)
      if (isInstitution) {
        sections.push({
          title: t('user:admin-team.permissions.section.research-teams'),
          choices: [
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_view_research_teams')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_view_research_teams_help')}
                  </Help>
                </span>
              ),
              value: CAN_VIEW_RESEARCH_TEAMS,
            },
            {
              text: (
                <span>
                  {t('user:admin-team.permissions.can_create_and_administrate_research_teams')}
                  <Help iconClassName="ml-2 align-middle">
                    {t('user:admin-team.permissions.can_create_and_administrate_research_teams_help')}
                  </Help>
                </span>
              ),
              value: CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
            },
          ],
        });
      }

      return sections;
    };

    getDisabledChoices = () => {
      const { writablePermissions } = this.props;
      const choices = this.getChoices();
      return choices
        .map((choice) => choice.value)
        .filter((choice) => !writablePermissions.includes(choice));
    };

    renderStandardPermissions = () => {
      const {
        t, value, readOnly, title, labelClassName, isOwnerAndLeader,
        showCannotEditOwnPermissions,
      } = this.props;
      const { internalValue } = this.state;

      const effectiveValue = internalValue || value;
      const choices = this.getChoices();
      const disabledChoices = this.getDisabledChoices();

      return (
        <LabeledChoice
          rowClassName="row text-left d-block"
          colLabelClassName="col-12"
          colInputClassName="col-12 text-left"
          multipleChoices
          orientation="vertical"
          hideOptionalLabel
          disabled={readOnly || isOwnerAndLeader || showCannotEditOwnPermissions}
          label={(
            <>
              <Label className={`mb-0 align-middle small${labelClassName ? ` ${labelClassName}` : ''}`}>
                {(typeof title === 'string') ? t(title) : title}
              </Label>
              <Help iconClassName="ml-2 align-middle">
                {t('user:team.permissions.member_help')}
              </Help>
            </>
          )}
          name="team-member-permissions"
          labelClassName="mb-2"
          value={effectiveValue}
          choices={choices}
          disabledChoices={isOwnerAndLeader
            || showCannotEditOwnPermissions ? choices.map((c) => c.value) : disabledChoices}
          onChange={this.onPermissionChange}
        />
      );
    };

    renderAdminTeamPermissions = () => {
      const {
        t, value, readOnly, title, labelClassName, isOwnerAndLeader,
        showCannotEditOwnPermissions,
      } = this.props;
      const { internalValue } = this.state;

      const effectiveValue = internalValue || value;
      const sections = this.getAdminTeamSections();
      const allChoices = this.getChoices();
      const disabledChoices = this.getDisabledChoices();

      const isDisabled = readOnly || isOwnerAndLeader || showCannotEditOwnPermissions;
      const effectiveDisabledChoices = isOwnerAndLeader || showCannotEditOwnPermissions
        ? allChoices.map((c) => c.value)
        : disabledChoices;

      return (
        <>
          <Label className={`mb-2 align-middle small${labelClassName ? ` ${labelClassName}` : ''}`}>
            {(typeof title === 'string') ? t(title) : title}
            <Help iconClassName="ml-2 align-middle">
              {t('user:team.permissions.member_help')}
            </Help>
          </Label>

          {sections.map((section, index) => (
            <div key={section.title} className={index > 0 ? 'mt-3' : ''}>
              <div className="font-weight-semibold text-underline mb-2 small">
                {section.title}
              </div>
              <LabeledChoice
                rowClassName="row text-left d-block"
                colLabelClassName="col-12"
                colInputClassName="col-12 text-left"
                multipleChoices
                orientation="vertical"
                hideOptionalLabel
                disabled={isDisabled}
                name={`team-member-permissions-${index}`}
                value={effectiveValue}
                choices={section.choices}
                disabledChoices={effectiveDisabledChoices}
                onChange={this.onPermissionChange}
              />
            </div>
          ))}
        </>
      );
    };

    render() {
      const {
        t, className, isOwnerAndLeader, showOwnerOnlyMessage, showCannotEditOwnPermissions,
        isAdminTeam,
      } = this.props;

      return (
        <div className={className}>
          {isOwnerAndLeader && (
            <div className="alert alert-info small mb-2">
              <FontAwesomeIcon icon={['fas', 'info-circle']} className="mr-2" />
              {t('user:team.permissions.owner_leader_info')}
            </div>
          )}

          {showOwnerOnlyMessage && (
            <div className="alert alert-warning small mb-2">
              <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} className="mr-2" />
              {t('user:team.permissions.only_owner_can_change_leader_permissions')}
            </div>
          )}

          {showCannotEditOwnPermissions && (
            <div className="alert alert-warning small mb-2">
              <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} className="mr-2" />
              {t('user:team.permissions.cannot_edit_own_permissions')}
            </div>
          )}

          {isAdminTeam
            ? this.renderAdminTeamPermissions()
            : this.renderStandardPermissions()}
        </div>
      );
    }
}

export default TeamMemberPermissionSettings;
